/**
 * Task Dependency Critical Path Service - Phase 4 Architecture
 *
 * Encapsulates critical path analysis, scheduling, and slack calculations.
 * Extracted from TaskDependencyService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse } from "@/services/core/service-registry";
import type { CriticalPath, TaskDependency } from "@/types/time-management";

interface TaskGraphNode {
  id: string;
  title: string;
  duration: number;
  dependencies: string[];
  dependents: string[];
  earliestStart?: number;
  latestStart?: number;
  slack?: number;
}

interface CriticalPathResult {
  tasks: string[];
  totalDuration: number;
  path: TaskGraphNode[];
}

export class TaskDependencyCriticalPathService implements IService {
  public readonly name = "TaskDependencyCriticalPathService";
  private readonly supabase = typedSupabase;

  async initialize(): Promise<void> {
    logger.info("✅ TaskDependencyCriticalPathService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskDependencyCriticalPathService disposed");
  }

  async calculateCriticalPath(
    taskIds: string[]
  ): Promise<ServiceResponse<CriticalPath>> {
    try {
      const { data: dependencies, error } = await this.supabase
        .from("task_dependencies")
        .select("*")
        .or(
          `predecessor_task_id.in.(${taskIds.join(",")}),successor_task_id.in.(${taskIds.join(",")})`
        );

      if (error) {
        return { success: false, error: error.message };
      }

      const { data: tasks, error: tasksError } = await this.supabase
        .from("todos")
        .select(
          `
          id,
          title,
          task_estimations(estimated_duration)
        `
        )
        .in("id", taskIds);

      if (tasksError) {
        return { success: false, error: tasksError.message };
      }

      const graph = this.buildTaskGraph(tasks || [], dependencies || []);
      const criticalPath = this.findCriticalPath(graph);

      const typedDependencies: TaskDependency[] = (dependencies || []).map(
        (dep: {
          id: string;
          predecessor_task_id: string;
          successor_task_id: string;
          dependency_type: string;
          lag_time: number | null;
          created_at: string | null;
          updated_at: string | null;
        }) => ({
          id: dep.id,
          predecessor_task_id: dep.predecessor_task_id,
          successor_task_id: dep.successor_task_id,
          dependency_type: dep.dependency_type as
            | "finish_to_start"
            | "start_to_start"
            | "finish_to_finish"
            | "start_to_finish",
          lag_time: dep.lag_time ?? 0,
          created_at: dep.created_at ?? new Date().toISOString(),
          updated_at: dep.updated_at ?? new Date().toISOString(),
        })
      );

      return {
        success: true,
        data: {
          tasks: criticalPath.tasks || [],
          total_duration: criticalPath.totalDuration || 0,
          critical_dependencies: typedDependencies,
          earliest_completion: new Date(
            Date.now() + (criticalPath.totalDuration || 0) * 60000
          ).toISOString(),
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async identifyCriticalTasks(
    projectId: string
  ): Promise<ServiceResponse<string[]>> {
    try {
      const { data: tasks, error } = await this.supabase
        .from("todos")
        .select("id")
        .eq("project_id", projectId);

      if (error) {
        return { success: false, error: error.message };
      }

      const taskIds = (tasks || []).map((task) => task.id);
      const criticalPathResult = await this.calculateCriticalPath(taskIds);

      if (!criticalPathResult.success) {
        return { success: false, error: criticalPathResult.error };
      }

      return { success: true, data: criticalPathResult.data?.tasks || [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async calculateTaskSlack(taskId: string): Promise<
    ServiceResponse<{
      totalSlack: number;
      freeSlack: number;
      isCritical: boolean;
    }>
  > {
    try {
      const { data: task, error } = await this.supabase
        .from("todos")
        .select("id, client_id, created_by")
        .eq("id", taskId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      let projectTasksQuery = this.supabase.from("todos").select("id");

      if (task.client_id) {
        projectTasksQuery = projectTasksQuery.eq("client_id", task.client_id);
      } else {
        projectTasksQuery = projectTasksQuery.eq("created_by", task.created_by ?? '');
      }

      const { data: projectTasks } = await projectTasksQuery;

      const taskIds = (projectTasks ?? []).map((t) => t.id);
      const scheduleResult = await this.getTaskSchedule(taskIds);

      if (!scheduleResult.success) {
        return { success: false, error: scheduleResult.error };
      }

      const taskSchedule = scheduleResult.data?.find(
        (s) => s.taskId === taskId
      );
      if (!taskSchedule) {
        return { success: false, error: "Task schedule not found" };
      }

      return {
        success: true,
        data: {
          totalSlack: taskSchedule.slack,
          freeSlack: taskSchedule.slack,
          isCritical: taskSchedule.slack === 0,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getTaskSchedule(
    taskIds: string[],
    projectStartDate?: string
  ): Promise<
    ServiceResponse<
      Array<{
        taskId: string;
        earlyStart: string;
        earlyFinish: string;
        lateStart: string;
        lateFinish: string;
        duration: number;
        slack: number;
      }>
    >
  > {
    try {
      const criticalPathResult = await this.calculateCriticalPath(taskIds);
      if (!criticalPathResult.success) {
        return { success: false, error: criticalPathResult.error };
      }

      const startDate = projectStartDate ? new Date(projectStartDate) : new Date();

      return {
        success: true,
        data: this.convertCriticalPathToSchedule(
          criticalPathResult.data!,
          startDate
        ),
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  private buildTaskGraph(
    tasks: Array<{
      id: string;
      title: string;
      task_estimations?: Array<{ estimated_duration: number }>;
    }>,
    dependencies: Array<{
      id: string;
      predecessor_task_id: string;
      successor_task_id: string;
      dependency_type: string;
      lag_time: number | null;
      created_at: string | null;
      updated_at: string | null;
    }>
  ): TaskGraphNode[] {
    const taskMap = new Map<string, TaskGraphNode>();
    tasks.forEach((task) => {
      taskMap.set(task.id, {
        id: task.id,
        title: task.title,
        duration: task.task_estimations?.[0]?.estimated_duration ?? 60,
        dependencies: [],
        dependents: [],
      });
    });

    dependencies.forEach((dep) => {
      const predecessor = taskMap.get(dep.predecessor_task_id);
      const successor = taskMap.get(dep.successor_task_id);
      if (predecessor && successor) {
        predecessor.dependents.push(successor.id);
        successor.dependencies.push(predecessor.id);
      }
    });

    return Array.from(taskMap.values());
  }

  private findCriticalPath(graph: TaskGraphNode[]): CriticalPathResult {
    const taskMap = new Map<string, TaskGraphNode>();
    graph.forEach((task) => taskMap.set(task.id, task));

    const calculateEarlyTimes = (
      taskId: string,
      memo: Map<string, number> = new Map()
    ): number => {
      if (memo.has(taskId)) {return memo.get(taskId)!;}

      const task = taskMap.get(taskId);
      if (!task) {return 0;}

      let maxEarlyStart = 0;
      task.dependencies.forEach((depId: string) => {
        const depTask = taskMap.get(depId);
        if (depTask) {
          const depEarlyFinish =
            calculateEarlyTimes(depId, memo) + depTask.duration;
          maxEarlyStart = Math.max(maxEarlyStart, depEarlyFinish);
        }
      });

      memo.set(taskId, maxEarlyStart);
      return maxEarlyStart;
    };

    const earlyTimes = new Map<string, number>();
    graph.forEach((task) => {
      earlyTimes.set(task.id, calculateEarlyTimes(task.id));
    });

    const criticalTasks = graph
      .filter((task) => {
        return task.dependencies.length === 0 || task.dependents.length === 0;
      })
      .map((task) => task.id);

    const totalDuration = Math.max(
      ...Array.from(earlyTimes.values()).map(
        (early, i) => early + graph[i].duration
      )
    );

    return {
      tasks: criticalTasks,
      totalDuration,
      path: graph.filter((task) => criticalTasks.includes(task.id)),
    };
  }

  private convertCriticalPathToSchedule(
    criticalPath: CriticalPath,
    startDate: Date
  ): Array<{
    taskId: string;
    earlyStart: string;
    earlyFinish: string;
    lateStart: string;
    lateFinish: string;
    duration: number;
    slack: number;
  }> {
    return criticalPath.tasks.map((taskId, index) => {
      const taskStart = new Date(
        startDate.getTime() + index * 60 * 60 * 1000
      );
      const taskEnd = new Date(taskStart.getTime() + 60 * 60 * 1000);

      return {
        taskId,
        earlyStart: taskStart.toISOString(),
        earlyFinish: taskEnd.toISOString(),
        lateStart: taskStart.toISOString(),
        lateFinish: taskEnd.toISOString(),
        duration: 60,
        slack: 0,
      };
    });
  }
}
